// Pure mappers between the ucd Content Portal API shapes and the FE view
// shapes used by the content-config pages. Keep these dependency-free so they
// are trivially testable and reusable across pages.

import {
  AudienceSourceType,
  ContentConfigStatus,
} from 'omega-rtc/types/ts/content-config';
import { v4 as uuid4 } from 'uuid';
import type {
  AudienceApiError,
  AudienceBodyApi,
  AudienceConfigApi,
  AudienceReference,
  ContentConfigCountry,
  ContentConfigMetaApi,
  ContentConfigVertical,
  CountryApi,
  EventBodyApi,
  EventConfigApi,
  FunnelObjective,
  ProductAudienceAssociation,
  ProductAudienceOption,
  ProductObjectiveApi,
  VerticalApi,
} from 'omega-rtc/types/ts/content-config';

export const STATUS_ACTIVE = 'CONTENT_CONFIG_STATUS_ACTIVE';
export const STATUS_PENDING = 'CONTENT_CONFIG_STATUS_PENDING_APPROVAL';
export const STATUS_REJECTED = 'CONTENT_CONFIG_STATUS_REJECTED';
export const STATUS_RECALLED = 'CONTENT_CONFIG_STATUS_RECALLED';

export const isPending = (status) => status === STATUS_PENDING;
export const isActive = (status) => status === STATUS_ACTIVE;
export const isTerminal = (status) => status === STATUS_REJECTED || status === STATUS_RECALLED;

// Display name from an email address (the local part before "@").
export const nameFromEmail = (email) => (email ? String(email).split('@')[0] : '');

// Per-language product content fields — the single source of truth that drives
// the editor tabs, the "fields filled" count, and the diff modal. `mode` is the
// DiffField render mode ('word' by default; 'line' for the CTA list).
export const PRODUCT_LANG_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'ciEnabled', label: 'CI Enabled', type: 'boolean' },
  { key: 'overview', label: 'Product overview' },
  { key: 'reason', label: 'Reason to use' },
  { key: 'cta', label: 'Call to action', mode: 'line' },
];

// Resolve a language code to its display name via the platform Intl API
// (cached), falling back to the uppercased code. Shared by every page that
// shows languages.
const LANG_DISPLAY = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' });
  } catch (e) {
    return null;
  }
})();
export const langLabel = (code) => {
  if (!code) return '';
  let name = null;
  try {
    name = LANG_DISPLAY && LANG_DISPLAY.of(code);
  } catch (e) {
    name = null;
  }
  return name && name.toLowerCase() !== code.toLowerCase() ? name : code.toUpperCase();
};

// Locale comparator for Duxton Table `sorter` props. Pass one or more field
// names; the first non-empty field on each row is used (e.g.
// `localeSorter('name', 'id')` sorts by name, falling back to id).
export const localeSorter = (...fields) => {
  const pick = (row) => fields.map((f) => row[f]).find(Boolean) || '';
  return (a, b) => String(pick(a)).localeCompare(String(pick(b)));
};

// Epoch seconds (or already-ms) → milliseconds.
const toMs = (n) => (n < 1e12 ? n * 1000 : n);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Absolute deadline as "Jun 13, 2026 6:43 PM" from epoch seconds (or ms).
// Returns "" when unset. Used for the pending-change expiry in the banner.
export const formatExpiry = (s) => {
  if (!s) return '';
  const n = Number(s);
  if (Number.isNaN(n)) return '';
  const d = new Date(toMs(n));
  if (Number.isNaN(d.getTime())) return '';
  const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
  const h12 = d.getHours() % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${h12}:${min} ${ampm}`;
};

// LCS-based diff of two token arrays → ops [{ type: 'eq'|'del'|'add', text }].
// 'del' = present in A (old) only; 'add' = present in B (new) only.
const lcsDiff = (A, B) => {
  const n = A.length;
  const m = B.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { ops.push({ type: 'eq', text: A[i] }); i += 1; j += 1; } else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ type: 'del', text: A[i] }); i += 1; } else { ops.push({ type: 'add', text: B[j] }); j += 1; }
  }
  while (i < n) { ops.push({ type: 'del', text: A[i] }); i += 1; }
  while (j < m) { ops.push({ type: 'add', text: B[j] }); j += 1; }
  return ops;
};

// Word-level diff (whitespace preserved as tokens so spacing is intact).
export const wordDiffOps = (oldStr, newStr) => lcsDiff(
  (oldStr || '').split(/(\s+)/).filter((t) => t !== ''),
  (newStr || '').split(/(\s+)/).filter((t) => t !== ''),
);

// Line-level diff (e.g. for the call-to-action list).
export const lineDiffOps = (oldStr, newStr) => lcsDiff(
  (oldStr || '').split('\n').map((l) => l.trim()).filter(Boolean),
  (newStr || '').split('\n').map((l) => l.trim()).filter(Boolean),
);

// `deeplink` is the Content Injection fallback for a Product Offering that
// defines none of its own, so it is carried through here rather than dropped.
// '' when the environment's UCM Content Portal config has no value.
export const verticalFromApi = (v: VerticalApi): ContentConfigVertical => ({
  id: v.code,
  name: v.name,
  deeplink: v.deeplink || '',
});

export const personaFromApi = (p) => ({ key: p.code, desc: p.description || '' });

export const attributeFromApi = (a) => ({
  key: a.key,
  definition: a.description || '',
  remarks: a.remarks || '',
});

export const attributeFromAudienceApi = (config) => {
  const body = config.body || {};
  return {
    key: String(body.source || '').replace(/^shared\./, ''),
    definition: body.description || '',
    remarks: '',
  };
};

export const productAttributeOptionsFromApi = ({
  attributes = [],
  audiences = [],
  audiencesEnabled = false,
}) => {
  if (!audiencesEnabled) return attributes.map(attributeFromApi);
  const seen = new Set();
  return audiences
    .filter((config) =>
      config.body && config.body.type === AudienceSourceType.Segment)
    .map(attributeFromAudienceApi)
    .filter((attribute) => {
      if (!attribute.key || seen.has(attribute.key)) return false;
      seen.add(attribute.key);
      return true;
    });
};

export const countryFromApi = (c: CountryApi): ContentConfigCountry => {
  // English is always the first/default language for every country (prepended
  // and de-duped) regardless of the order the API returns.
  const apiLangs = c.languages || [];
  const langs = ['en', ...apiLangs.filter((l) => l && l.toLowerCase() !== 'en')];
  return {
    id: c.code,
    name: c.name,
    langs,
    // The UCM whitelist verbatim. `langs` above is synthesised, so it must not
    // be used for a value sent back as an association `lang` — UCD matches it
    // byte-for-byte (e.g. TW configures "zh-Hant-TW").
    apiLangs,
    currencies: c.currencies || [],
    defaultLang: c.default_language || '',
  };
};

const metaFromApi = (meta: Partial<ContentConfigMetaApi> = {}) => ({
  id: meta.id,
  entity: meta.entity,
  country: meta.country,
  key: meta.key,
  status: meta.status,
  expiresAt: meta.expires_at,
  createdAt: meta.created_at,
  updatedAt: meta.updated_at,
  createdBy: meta.created_by,
  updatedBy: meta.updated_by,
  reviewedAt: meta.reviewed_at || 0,
  reviewedBy: meta.reviewed_by || '',
  reviewComment: meta.review_comment || '',
});

export const cvpFromApi = (cfg) => {
  const body = cfg.body || {};
  return {
    ...metaFromApi(cfg.meta),
    desc: body.description || '',
  };
};

// ---- Events (Seasonality Phase 1) ----
// Calendar-date format for the Event start/end pickers. Equals the backend's
// eventDateLayout ("2006-01-02"), which is what the API stores and returns.
export const EVENT_DATE_FORMAT = 'YYYY-MM-DD';

export const eventFromApi = (cfg: EventConfigApi) => {
  const body = cfg.body || ({} as EventBodyApi);
  return {
    ...metaFromApi(cfg.meta),
    name: body.name || '',
    description: body.description || '',
    startDate: body.start_date || '',
    endDate: body.end_date || '',
  };
};

// Trimmed on the way out to match the backend's normalizeEvent, so a padded
// value is never persisted and never counts as a local edit.
export const eventToApiBody = (event): EventBodyApi => ({
  start_date: String((event && event.startDate) || '').trim(),
  end_date: String((event && event.endDate) || '').trim(),
  description: String((event && event.description) || '').trim(),
  name: String((event && event.name) || '').trim(),
});

// Field-wise comparison of the submitted bodies — Event has no order-sensitive
// collections, so the trimmed bodies compare directly.
export const sameEventBody = (left, right): boolean => {
  const a = eventToApiBody(left);
  const b = eventToApiBody(right);
  return a.start_date === b.start_date
    && a.end_date === b.end_date
    && a.description === b.description
    && a.name === b.name;
};

export const audienceFromApi = (cfg: AudienceConfigApi) => {
  const body = cfg.body || ({} as AudienceBodyApi);
  return {
    ...metaFromApi(cfg.meta),
    type: body.type,
    name: body.name || '',
    description: body.description || '',
    source: body.source || '',
    personaVariables: (body.persona_variables || []).map((variable) => ({
      name: variable.name,
      description: variable.description || '',
    })),
    deeplink: {
      names: (body.deeplink && body.deeplink.names) || [],
      deepLinkTemplate: (body.deeplink && body.deeplink.deep_link_template) || '',
    },
  };
};

export const audienceToApiBody = (audience): AudienceBodyApi => {
  const body: AudienceBodyApi = {
    type: audience.type,
    name: String(audience.name || '').trim(),
    description: String(audience.description || '').trim(),
    source: String(audience.source || '').trim(),
    persona_variables: audience.type === AudienceSourceType.Segment
      ? []
      : (audience.personaVariables || []).map((variable) => ({
        name: String(variable.name || '').trim(),
        description: String(variable.description || '').trim(),
      })),
  };
  const deeplink = audience.deeplink || {};
  const deeplinkNames = audience.type === AudienceSourceType.Segment
    ? []
    : (deeplink.names || []).map((name) => String(name || '').trim());
  const deepLinkTemplate = audience.type === AudienceSourceType.Segment
    ? ''
    : String(deeplink.deepLinkTemplate || '').trim();
  if (deeplinkNames.length || deepLinkTemplate) {
    body.deeplink = {
      names: deeplinkNames,
      deep_link_template: deepLinkTemplate,
    };
  }
  return body;
};

export const MAX_AUDIENCE_REASON_LENGTH = 1000;

export const normalizeAudienceReason = (reason = '') => String(reason).trim();

export const audienceReasonLength = (reason = '') => Array.from(normalizeAudienceReason(reason)).length;

export const isAudienceReasonValid = (reason = '') =>
  audienceReasonLength(reason) <= MAX_AUDIENCE_REASON_LENGTH;

// An association language is valid when it is unset (not language-targeted) or
// an exact member of the country's UCM whitelist. Deliberately not trimmed or
// case-folded: UCD compares byte-for-byte and never normalises, so "MS" and
// " ms " are rejected rather than corrected.
export const isAudienceLangValid = (lang = '', countryLangs: string[] = []) =>
  lang === '' || countryLangs.includes(lang);

export const normalizeProductAudienceAssociations = (
  audiences: Array<string | {
    audience_key?: string;
    audienceKey?: string;
    reason_to_use?: string;
    reasonToUse?: string;
    lang?: string;
  }> = [],
): ProductAudienceAssociation[] => {
  const normalized: ProductAudienceAssociation[] = [];
  const indexByKey = new Map<string, number>();

  audiences.forEach((association) => {
    const audienceKey = typeof association === 'string'
      ? association
      : association.audience_key || association.audienceKey || '';
    if (!audienceKey) return;

    const reasonToUse = typeof association === 'string'
      ? ''
      : association.reason_to_use || association.reasonToUse || '';
    // Kept verbatim — see isAudienceLangValid.
    const lang = typeof association === 'string' ? '' : association.lang || '';
    const existingIndex = indexByKey.get(audienceKey);
    if (existingIndex === undefined) {
      indexByKey.set(audienceKey, normalized.length);
      normalized.push({ audienceKey, reasonToUse, lang });
      return;
    }

    const existing = normalized[existingIndex];
    if (!existing.reasonToUse && reasonToUse) {
      normalized[existingIndex] = { ...normalized[existingIndex], reasonToUse };
    }
    if (!existing.lang && lang) {
      normalized[existingIndex] = { ...normalized[existingIndex], lang };
    }
  });

  return normalized;
};

export const sameAudienceAssociations = (
  left: ProductAudienceAssociation[] = [],
  right: ProductAudienceAssociation[] = [],
) => {
  const comparable = (items) => normalizeProductAudienceAssociations(items)
    .map(({ audienceKey, reasonToUse, lang }) =>
      `${audienceKey}\u0001${normalizeAudienceReason(reasonToUse)}\u0001${lang}`)
    .sort();
  const a = comparable(left);
  const b = comparable(right);
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

// ---- Product funnel objectives (RTC-33546) ----
// Public HTTP `ID` <-> FE `id`; '' means a new objective, so `ID` is omitted
// on the way out and UCD mints the UUID. Existing ids are passed through
// unchanged — the update API rejects unknown or missing known ids.

const funnelObjectiveFromApi = (objective: ProductObjectiveApi): FunnelObjective => ({
  id: objective.ID || '',
  name: objective.name || '',
  description: objective.description || '',
  source: objective.source || '',
});

const funnelObjectiveToApi = (objective: FunnelObjective): ProductObjectiveApi => ({
  ...(objective.id ? { ID: objective.id } : {}),
  name: (objective.name || '').trim(),
  description: (objective.description || '').trim(),
  source: (objective.source || '').trim(),
});

// A generation run names exactly one objective, but UCD mints the objective id
// only on save — so an objective authored on a DRAFT, or added as a local edit,
// has no id to name it by. `localID` is that identity: minted in the browser,
// used for the dropdown and for the inline productConfig Mystique matches
// against, and never sent on create/update.
export interface GenerationFunnelObjective extends FunnelObjective {
  generationID: string;
}

const UNSAVED_OBJECTIVE_ID_PREFIX = 'unsaved-objective-';

// Called on apply, the only way objectives enter the page's edit sandbox, so
// everything downstream can rely on an id or a localID being present.
export const ensureFunnelObjectiveLocalIDs = (
  objectives: FunnelObjective[] = [],
): FunnelObjective[] => objectives.map((objective) => (
  (objective.id || '').trim() || (objective.localID || '').trim()
    ? objective
    : { ...objective, localID: uuid4() }
));

// One id per objective, preferring the one UCD owns. The positional fallback is
// unreachable from the editor because apply normalizes first; it stays so that a
// hand-built objective cannot silently drop out of the generation dropdown the
// way an id-less one used to.
export const resolveGenerationObjectives = (
  objectives: FunnelObjective[] = [],
): GenerationFunnelObjective[] => {
  const seenIDs = new Set<string>();
  const resolved: GenerationFunnelObjective[] = [];

  objectives.forEach((objective, index) => {
    const generationID = (objective.id || '').trim()
      || (objective.localID || '').trim()
      || `${UNSAVED_OBJECTIVE_ID_PREFIX}${index}`;
    if (seenIDs.has(generationID)) return;
    seenIDs.add(generationID);
    resolved.push({ ...objective, generationID });
  });

  return resolved;
};

// Mirrors the backend rule: name, description and source are required and
// rejected when blank, so incomplete objectives are never submitted.
export const isFunnelObjectiveComplete = (objective: FunnelObjective): boolean => Boolean(
  (objective.name || '').trim()
  && (objective.description || '').trim()
  && (objective.source || '').trim(),
);

// Order-sensitive per-field comparison ('' id = new objective); trimmed like
// the submitted body so whitespace-only edits are not a change.
export const sameFunnelObjectives = (
  left: FunnelObjective[] = [],
  right: FunnelObjective[] = [],
) => {
  const comparable = (objectives) => objectives.map((objective) => [
    objective.id,
    (objective.name || '').trim(),
    (objective.description || '').trim(),
    (objective.source || '').trim(),
  ].join('\u0001'));
  const a = comparable(left);
  const b = comparable(right);
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

export const normalizeProductDeeplink = (deeplink = '') => String(deeplink).trim();

export const isProductDeeplinkValid = (deeplink = '') => {
  const value = String(deeplink || '');
  return value === '' || normalizeProductDeeplink(value).length > 0;
};

// Tracking params UCD stamps onto every Content Injection deeplink (RTC-34009).
// Mirrors UCD utils.ReservedDeeplinkTrackingParams / IsReservedDeeplinkTrackingName.
export const RESERVED_DEEPLINK_PARAMS = [
  'sourceID',
  'sourceCampaignName',
];

const sameParamName = (left: string, right: string) =>
  left.toLowerCase() === right.toLowerCase();

// Decodes a raw "key=value" query pair's key once, as a query parser would, so an
// encoded key such as "source%49D" still matches. A malformed escape stays as written.
const deeplinkPairKey = (pair: string) => {
  const key = pair.split('=')[0];
  try {
    return decodeURIComponent(key.replace(/\+/g, ' '));
  } catch (error) {
    return key;
  }
};

export const isReservedDeeplinkName = (name = ''): boolean => {
  const trimmed = String(name || '').trim();
  return RESERVED_DEEPLINK_PARAMS.some((reserved) => sameParamName(trimmed, reserved));
};

// UCD rejects a Presto/Trino view as a table Audience source with InvalidArgument
// (RTC-34713). Not-found (404) and unclassifiable or upstream failures (503) differ.
export const isPrestoViewError = (error?: AudienceApiError | null): boolean =>
  Boolean(error)
  && error.status === 400
  && /is a Presto view/i.test(String(error.message || ''));

/** Reserved params used as query keys in deeplink, in RESERVED_DEEPLINK_PARAMS order. */
export const reservedDeeplinkParams = (deeplink = ''): string[] => {
  const base = String(deeplink || '').split('#')[0];
  const queryStart = base.indexOf('?');
  if (queryStart === -1) return [];
  const keys = base.slice(queryStart + 1)
    .split('&')
    .filter(Boolean)
    .map(deeplinkPairKey);
  return RESERVED_DEEPLINK_PARAMS.filter((reserved) =>
    keys.some((key) => sameParamName(key, reserved)));
};

export const audienceReferenceFromApi = (cfg): AudienceReference => {
  const meta = cfg.meta || {};
  const body = cfg.body || {};
  return {
    key: meta.key || '',
    name: body.name || '',
    country: meta.country || '',
    sourceType: body.type || AudienceSourceType.Unspecified,
    status: meta.status || ContentConfigStatus.Unspecified,
  };
};

export type AudienceLabelCopy = {
  regional: string;
  segment: string;
  table: string;
  unavailablePrefix: string;
  unknownSource: string;
};

const DEFAULT_AUDIENCE_LABEL_COPY: AudienceLabelCopy = {
  regional: 'Regional',
  segment: 'Segment',
  table: 'Table',
  unavailablePrefix: 'Unavailable Audience',
  unknownSource: 'Unknown source',
};

const audienceSourceTypeLabel = (
  sourceType: AudienceSourceType,
  labels: AudienceLabelCopy,
) => {
  if (sourceType === AudienceSourceType.Segment) return labels.segment;
  if (sourceType === AudienceSourceType.Table) return labels.table;
  return labels.unknownSource;
};

export const isAudienceEligibleForProduct = (
  audience: AudienceReference,
  productCountry: string,
) => audience.status === ContentConfigStatus.Active
  && (!audience.country || audience.country === productCountry);

export const audienceDisplayLabel = (
  audience: AudienceReference,
  countryName: (country: string) => string = (country) => country,
  labels: AudienceLabelCopy = DEFAULT_AUDIENCE_LABEL_COPY,
) => [
  audience.name || audience.key,
  audienceSourceTypeLabel(audience.sourceType, labels),
  audience.country ? countryName(audience.country) || audience.country : labels.regional,
].join(' · ');

export const buildProductAudienceOptions = (
  audiences: AudienceReference[] = [],
  selectedAssociations: ProductAudienceAssociation[] = [],
  productCountry: string,
  countryName: (country: string) => string = (country) => country,
  labels: AudienceLabelCopy = DEFAULT_AUDIENCE_LABEL_COPY,
): ProductAudienceOption[] => {
  const byKey = new Map<string, AudienceReference>();
  audiences.forEach((audience) => {
    if (!audience.key) return;
    const current = byKey.get(audience.key);
    if (!current || (
      isAudienceEligibleForProduct(audience, productCountry)
      && !isAudienceEligibleForProduct(current, productCountry)
    )) {
      byKey.set(audience.key, audience);
    }
  });

  const eligible = Array.from(byKey.values())
    .filter((audience) => isAudienceEligibleForProduct(audience, productCountry));
  const optionKeys = new Set(eligible.map((audience) => audience.key));
  const selectedFallbacks = normalizeProductAudienceAssociations(selectedAssociations)
    .filter(({ audienceKey }) => !optionKeys.has(audienceKey))
    .map(({ audienceKey }) => byKey.get(audienceKey) || {
      key: audienceKey,
      name: '',
      country: '',
      sourceType: AudienceSourceType.Unspecified,
      status: ContentConfigStatus.Unspecified,
    });

  return [...eligible, ...selectedFallbacks]
    .map((audience) => {
      const isEligible = isAudienceEligibleForProduct(audience, productCountry);
      let unavailableReason = '';
      if (!isEligible && audience.status !== ContentConfigStatus.Active) {
        unavailableReason = 'inactive or missing';
      } else if (!isEligible) {
        unavailableReason = 'not available for this country';
      }
      const label = audience.name
        ? audienceDisplayLabel(audience, countryName, labels)
        : `${labels.unavailablePrefix} · ${audience.key}`;
      return {
        ...audience,
        eligible: isEligible,
        label,
        unavailableReason,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Format an API timestamp as "DD-MM-YYYY, HH:MM AM/PM". The backend returns
// epoch seconds (numeric), so scale to ms; also tolerate epoch-ms and ISO strings.
export const formatDateTime = (s) => {
  if (!s) return '—';
  let ms;
  if (typeof s === 'number' || /^\d+$/.test(String(s))) {
    ms = toMs(Number(s)); // seconds → ms
  } else {
    ms = Date.parse(s);
  }
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
  const h12 = d.getHours() % 12 || 12;
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}, ${pad(h12)}:${pad(d.getMinutes())} ${ampm}`;
};

// Product: API config_by_lang[lang] -> FE byLang[lang]; CTA []string <-> \n-string.
export const productFromApi = (cfg) => {
  const body = cfg.body || {};
  const byLang = {};
  const cbl = body.config_by_lang || {};
  Object.keys(cbl).forEach((lang) => {
    const lc = cbl[lang] || {};
    byLang[lang] = {
      name: lc.name || '',
      ciEnabled: Boolean(lc.ci_enabled),
      overview: lc.product_overview || '',
      reason: lc.reason_to_use || '',
      cta: (lc.call_to_action || []).join('\n'),
    };
  });
  return {
    ...metaFromApi(cfg.meta),
    name: body.name || '',
    vertical: body.vertical,
    deeplink: body.deeplink || '',
    byLang,
    whitelist: {
      cvps: body.cvps || [],
      attrs: body.attributes || [],
      personas: body.personas || [],
      audiences: normalizeProductAudienceAssociations(body.audiences || []),
      objectives: (body.objectives || []).map(funnelObjectiveFromApi),
    },
  };
};

// FE editor state -> API Product body for create/update.
export const productToApiBody = (fe) => {
  const configByLang = {};
  Object.keys(fe.byLang || {}).forEach((lang) => {
    const l = fe.byLang[lang] || {};
    const hasRequiredContent = Boolean((l.name || '').trim() && (l.overview || '').trim());
    configByLang[lang] = {
      name: l.name || '',
      ci_enabled: hasRequiredContent && Boolean(l.ciEnabled),
      product_overview: l.overview || '',
      reason_to_use: l.reason || '',
      call_to_action: (l.cta || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    };
  });
  const name = ((fe && fe.name) || '').trim();
  const body = {
    name: name || (fe.byLang && fe.byLang.en && fe.byLang.en.name) || '',
    vertical: fe.vertical,
    config_by_lang: configByLang,
    cvps: (fe.whitelist && fe.whitelist.cvps) || [],
    attributes: (fe.whitelist && fe.whitelist.attrs) || [],
    personas: (fe.whitelist && fe.whitelist.personas) || [],
    audiences: normalizeProductAudienceAssociations(
      (fe.whitelist && fe.whitelist.audiences) || [],
    ).map(({ audienceKey, reasonToUse, lang }) => ({
      audience_key: audienceKey,
      reason_to_use: normalizeAudienceReason(reasonToUse),
      // Passed through unchanged: UCD validates by exact match and never
      // normalises. '' means the association is not language-targeted.
      lang,
    })),
  };
  // No objectives -> key omitted: a product without objectives keeps the exact
  // legacy body (and the backend's legacy campaign flow).
  const objectives = ((fe.whitelist && fe.whitelist.objectives) || []).map(funnelObjectiveToApi);
  const bodyWithObjectives = objectives.length ? { ...body, objectives } : body;
  const deeplink = normalizeProductDeeplink(fe && fe.deeplink);
  return deeplink ? { ...bodyWithObjectives, deeplink } : bodyWithObjectives;
};

// Pick the display name for a product in the active language, falling back to
// English, then any language, then the opaque key.
export const productDisplayName = (feProduct, lang) => {
  const bl = feProduct.byLang || {};
  const languageValues = Object.values(bl) as Array<{ name?: string }>;
  const firstNamedLanguage = languageValues
    .find((value) => value && value.name);
  return (
    feProduct.name
    || (bl[lang] && bl[lang].name)
    || (bl.en && bl.en.name)
    || (firstNamedLanguage && firstNamedLanguage.name)
    || feProduct.key
  );
};
